import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../attendance/Attendance.css";

/**
 * Same shell as attendance modals: `.att-modal-overlay`, `.att-modal`, `.att-modal-header`, etc.
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   title: React.ReactNode,
 *   subtitle?: React.ReactNode,
 *   children: React.ReactNode,
 *   footer?: React.ReactNode,
 *   size?: 'medium' | 'large' | 'xlarge',
 *   titleId?: string,
 *   variant?: 'salary',
 *   headerIcon?: React.ReactNode,
 * }} props
 */
const PayrollModal = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = "medium",
  titleId = "payroll-modal-title",
  variant,
  headerIcon,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const sizeClass =
    size === "large" ? "payroll-modal-shell--large" : size === "xlarge" ? "payroll-modal-shell--xlarge" : "";
  const variantClass = variant === "salary" ? "payroll-modal-shell--salary-sc" : "";
  const headerSalaryClass = variant === "salary" ? " payroll-modal-header--salary" : "";
  const stackClass = subtitle || headerIcon ? " payroll-modal-header--stacked" : "";

  return (
    <div
      className="att-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`att-modal payroll-modal-shell ${sizeClass} ${variantClass}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={`att-modal-header${stackClass}${headerSalaryClass}`.trim()}>
          <div className={`payroll-modal-header-stack${headerIcon ? " payroll-modal-header-stack--with-icon" : ""}`}>
            {headerIcon ? (
              <span className="payroll-modal-header-icon" aria-hidden>
                {headerIcon}
              </span>
            ) : null}
            <div className="payroll-modal-header-text">
              <h2 className={`att-modal-title mb-0${variant === "salary" ? " payroll-modal-title--sentence" : ""}`} id={titleId}>
                {title}
              </h2>
              {subtitle ? (
                <p className="payroll-modal-subtitle mb-0" id={`${titleId}-subtitle`}>
                  {subtitle}
                </p>
              ) : null}
            </div>
          </div>
          <button type="button" className="att-modal-close" onClick={onClose} aria-label={t("app.payroll.common.modalClose")}>
            ×
          </button>
        </div>
        <div className={`att-modal-body payroll-modal-body${variant === "salary" ? " payroll-modal-body--salary-sc" : ""}`.trim()}>
          {children}
        </div>
        {footer ? (
          <div className={`att-modal-footer payroll-modal-footer${variant === "salary" ? " payroll-modal-footer--salary-sc" : ""}`.trim()}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PayrollModal;
